import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface StaticData {
  id: number;
  type: 'banner' | 'ceo' | 'application';
  title?: string;
  content?: string;
  icon?: string;
  link?: string;
  ceo_name?: string;
  ceo_position?: string;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface StaticDataResponse {
  success: boolean;
  data: StaticData[];
  message?: string;
}

class StaticDataService {
  private baseURL = `${API_BASE_URL}/mock-static-data`; // Using mock data for testing

  // Get all static data or filter by type
  async getStaticData(type?: 'banner' | 'ceo' | 'application'): Promise<StaticData[]> {
    try {
      const url = type ? `${this.baseURL}?type=${type}` : this.baseURL;
      const response = await axios.get<StaticDataResponse>(url);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch static data');
      }
    } catch (error) {
      console.error('Error fetching static data:', error);
      throw error;
    }
  }

  // Get banner data
  async getBannerData(): Promise<StaticData[]> {
    return this.getStaticData('banner');
  }

  // Get CEO message data
  async getCEOData(): Promise<StaticData[]> {
    return this.getStaticData('ceo');
  }

  // Get application data
  async getApplicationData(): Promise<StaticData[]> {
    return this.getStaticData('application');
  }

  // Get static data by type (alias for getStaticData)
  async getStaticDataByType(type: 'banner' | 'ceo' | 'application'): Promise<StaticData[]> {
    return this.getStaticData(type);
  }

  // Get single static data by ID
  async getStaticDataById(id: number): Promise<StaticData> {
    try {
      const response = await axios.get<{ success: boolean; data: StaticData; message?: string }>(`${this.baseURL}/${id}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch static data');
      }
    } catch (error) {
      console.error('Error fetching static data by ID:', error);
      throw error;
    }
  }
}

export default new StaticDataService();
